import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Button, Spinner } from 'react-bootstrap';
import { FaSync } from 'react-icons/fa';
import jwt_decode from 'jwt-decode';
import ObjectConfigApi from '../../../api/ObjectConfigApi';
import { fetchAiChatbotIndexingOptions } from '../../../utils/aiChatbotIndexingMode';
import AiSchemaCatalogSection from './schema/AiSchemaCatalogSection';

function formatIndexedSummary(indexed, { isStandard = false } = {}) {
  if (!indexed || typeof indexed !== 'object') return null;
  const parts = [];
  if (indexed.tables != null) {
    parts.push(
      isStandard
        ? `${indexed.tables} table embeddings (column names included in each)`
        : `${indexed.tables} tables`
    );
  }
  if (indexed.fields != null) {
    parts.push(
      indexed.fields === 0 && isStandard
        ? '0 per-field notes (optional field notes in catalog)'
        : `${indexed.fields} field embeddings`
    );
  }
  if (indexed.relationships != null) parts.push(`${indexed.relationships} relationships`);
  return parts.length ? parts.join(', ') : null;
}

const PAGE_CONFIG = {
  standard: {
    title: 'Index Standard Schema',
    subtitle:
      'Select standard CRM objects, add descriptions, save the catalog, then index for AI search.',
    canIndexKey: 'canIndexStandard',
    deniedMessage: 'Standard schema indexing is only available for product builder tenants.',
    indexFn: () => ObjectConfigApi.indexStandardSchemaEmbeddings(),
    successMessage: (summary) =>
      summary
        ? `Standard schema indexed successfully (${summary}).`
        : 'Standard schema indexed successfully.',
    indexingLabel: 'Indexing standard schema…',
    indexButtonLabel: 'Index standard schema',
  },
  custom: {
    title: 'Index Custom Schema',
    subtitle:
      'Select custom objects and custom fields, save the catalog, then index for AI search.',
    canIndexKey: 'canIndexCustom',
    deniedMessage: 'Custom schema indexing is not enabled for this tenant.',
    indexFn: () => ObjectConfigApi.indexTenantSchemaEmbeddings(),
    successMessage: (summary) =>
      summary
        ? `Custom schema indexed successfully (${summary}).`
        : 'Custom schema indexed successfully.',
    indexingLabel: 'Indexing custom schema…',
    indexButtonLabel: 'Index custom schema',
  },
};

const AiSchemaIndexingPage = ({ mode = 'custom' }) => {
  const config = PAGE_CONFIG[mode] || PAGE_CONFIG.custom;
  const isStandard = mode === 'standard';

  const isAdmin = useMemo(() => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return false;
      const decoded = jwt_decode(token);
      return String(decoded?.role_name || '').toUpperCase() === 'ADMIN';
    } catch {
      return false;
    }
  }, []);

  const [options, setOptions] = useState(null);
  const [loadingOptions, setLoadingOptions] = useState(true);
  const [indexing, setIndexing] = useState(false);
  const [catalogState, setCatalogState] = useState({
    savedIncludedCount: 0,
    draftIncludedCount: 0,
    isDirty: false,
  });
  const [message, setMessage] = useState({ variant: '', text: '' });
  const [lastResult, setLastResult] = useState(null);

  const loadOptions = useCallback(async () => {
    setLoadingOptions(true);
    try {
      const opts = await fetchAiChatbotIndexingOptions();
      setOptions(opts);
    } catch (err) {
      setMessage({
        variant: 'danger',
        text: err?.message || 'Failed to load indexing options.',
      });
      setOptions({
        canAccessSetup: false,
        canIndexStandard: false,
        canIndexCustom: false,
      });
    } finally {
      setLoadingOptions(false);
    }
  }, []);

  useEffect(() => {
    loadOptions();
  }, [loadOptions]);

  const canAccessPage = options?.[config.canIndexKey] === true;

  const handleIndex = async () => {
    if (catalogState.isDirty) {
      setMessage({
        variant: 'warning',
        text: 'Save the schema catalog before indexing.',
      });
      return;
    }
    if (catalogState.savedIncludedCount === 0) {
      setMessage({
        variant: 'warning',
        text: 'Select and save at least one object before indexing.',
      });
      return;
    }
    setMessage({ variant: '', text: '' });
    setIndexing(true);
    try {
      const res = await config.indexFn();
      if (!res?.success) {
        throw new Error(res?.message || 'Schema indexing failed.');
      }
      setLastResult(res.indexed);
      const summary = formatIndexedSummary(res.indexed, { isStandard });
      setMessage({
        variant: 'success',
        text: config.successMessage(summary),
      });
    } catch (err) {
      setMessage({
        variant: 'danger',
        text: err?.message || 'Schema indexing failed.',
      });
    } finally {
      setIndexing(false);
    }
  };

  if (!isAdmin) {
    return (
      <Alert variant="warning">Only administrators can manage AI Chatbot schema indexing.</Alert>
    );
  }

  if (loadingOptions) {
    return (
      <div className="text-center py-4">
        <Spinner animation="border" role="status" className="me-2" />
        <span>Loading…</span>
      </div>
    );
  }

  if (!options?.canAccessSetup) {
    return (
      <Alert variant="info">
        AI Chatbot schema indexing is not enabled for this tenant. Enable the AI Chatbot package
        or contact your administrator.
      </Alert>
    );
  }

  if (!canAccessPage) {
    return <Alert variant="info">{config.deniedMessage}</Alert>;
  }

  return (
    <div>
      <div className="d-flex flex-wrap align-items-start justify-content-between gap-3 mb-3">
        <div className="flex-grow-1">
          <h5 className="mb-1">{config.title}</h5>
          <p className="text-muted small mb-0">{config.subtitle}</p>
        </div>
        <Button
          variant="primary"
          className="flex-shrink-0"
          disabled={
            indexing
            || catalogState.savedIncludedCount === 0
            || catalogState.isDirty
          }
          onClick={handleIndex}
        >
          {indexing ? (
            <>
              <Spinner animation="border" size="sm" className="me-2" />
              {config.indexingLabel}
            </>
          ) : (
            <>
              <FaSync className="me-2" />
              {config.indexButtonLabel}
            </>
          )}
        </Button>
      </div>

      <p className="small text-muted mb-3">
        {catalogState.savedIncludedCount > 0
          ? `${catalogState.savedIncludedCount} object(s) saved and ready to index.`
          : 'Check objects and save before indexing.'}
        {catalogState.isDirty ? ' You have unsaved changes.' : ''}
        {lastResult && (
          <>
            {' '}
            <span className="text-success">
              Last run: {formatIndexedSummary(lastResult, { isStandard }) || 'completed'}
            </span>
          </>
        )}
      </p>

      {message.text && (
        <Alert
          variant={message.variant || 'info'}
          dismissible
          onClose={() => setMessage({ variant: '', text: '' })}
        >
          {message.text}
        </Alert>
      )}

      <AiSchemaCatalogSection
        mode={mode}
        disabled={indexing}
        onCatalogStateChange={setCatalogState}
      />

      {indexing && (
        <div
          className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
          style={{ backgroundColor: 'rgba(255,255,255,0.65)', zIndex: 1050 }}
        >
          <div className="text-center p-4 bg-white rounded shadow">
            <Spinner animation="border" className="mb-3" />
            <div className="fw-semibold">{config.indexingLabel}</div>
            <div className="text-muted small mt-1">This may take a few minutes. Please wait.</div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AiSchemaIndexingPage;
